import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowRightLeft
} from 'lucide-react';


const AdminRegistroPrenotazioni = () => {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'month'
  const [dateMode, setDateMode] = useState('booking_date'); // 'booking_date' or 'check_in'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthSummary, setMonthSummary] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [showMatchPanel, setShowMatchPanel] = useState(false);
  const [cleaningAlert, setCleaningAlert] = useState(null);
  const [cleaningInput, setCleaningInput] = useState('');

  const token = localStorage.getItem('hw_token');

  const fetchDayEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/booking-register?entry_date=${selectedDate}&date_mode=${dateMode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
        setTotals(data.totals || null);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate, dateMode]);

  const fetchMonthEntries = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        fetch(`${API}/api/booking-register?month=${selectedMonth}&date_mode=${dateMode}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        dateMode === 'booking_date'
          ? fetch(`${API}/api/booking-register/summary/monthly?month=${selectedMonth}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          : Promise.resolve(null)
      ]);
      
      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(data.entries || []);
        setTotals(data.totals || null);
      }
      
      if (summaryRes && summaryRes.ok) {
        const summary = await summaryRes.json();
        setMonthSummary(summary);
      } else if (!summaryRes) {
        setMonthSummary(null);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [token, selectedMonth, dateMode]);

  useEffect(() => {
    if (viewMode === 'day') {
      fetchDayEntries();
    } else {
      fetchMonthEntries();
    }
  }, [viewMode, fetchDayEntries, fetchMonthEntries]);

  const runMatchHW = async () => {
    try {
      const param = viewMode === 'day' ? `entry_date=${selectedDate}` : `month=${selectedMonth}`;
      const response = await fetch(`${API}/api/booking-register/match-hw?${param}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMatchResults(data);
        setShowMatchPanel(true);
        toast.success(`Incrocio completato: ${data.matched} match, ${data.discrepancies} discrepanze, ${data.not_found} non trovati`);
      }
    } catch (error) {
      toast.error('Errore nell\'incrocio');
    }
  };

  const handleCleaningAlert = (entry) => {
    setCleaningAlert(entry);
    setCleaningInput('');
  };

  const confirmNoCleaning = () => {
    toast.success(`Confermato: nessun costo pulizia per ${cleaningAlert.property_name}`);
    setCleaningAlert(null);
  };

  const submitCleaningCost = async () => {
    const cost = parseFloat(cleaningInput);
    if (isNaN(cost) || cost <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    try {
      const res = await fetch(`${API}/api/booking-register/update-property-cleaning`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: cleaningAlert.property_id, cleaning_cost: cost })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setCleaningAlert(null);
        if (viewMode === 'day') fetchDayEntries();
        else fetchMonthEntries();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
  };

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const changeMonth = (months) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + months, 1);
    setSelectedMonth(newDate.toISOString().slice(0, 7));
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '-';
    return `€${parseFloat(value).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Registro Prenotazioni - Admin</h1>
          <p className="text-sm text-slate-500">
            Vista completa con tutti i dati finanziari
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 p-1 rounded-lg">
            <Button
              variant={viewMode === 'day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('day')}
            >
              Giorno
            </Button>
            <Button
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('month')}
            >
              Mese
            </Button>
          </div>

          {/* Date Mode Toggle: filtra per data prenotazione o per data check-in */}
          <div className="flex bg-slate-100 p-1 rounded-lg">
            <Button
              variant={dateMode === 'booking_date' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDateMode('booking_date')}
              data-testid="registro-mode-booking-date"
            >
              Prenotazione
            </Button>
            <Button
              variant={dateMode === 'check_in' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDateMode('check_in')}
              data-testid="registro-mode-check-in"
            >
              Check-in
            </Button>
          </div>

          {/* Date Navigation */}
          {viewMode === 'day' ? (
            <>
              <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
              <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="icon" onClick={() => changeMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-40"
              />
              <Button variant="outline" size="icon" onClick={() => changeMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          <Button variant="outline" onClick={runMatchHW}>
            <ArrowRightLeft className="h-4 w-4 mr-2" /> Incrocia HW
          </Button>
          <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={async () => {
            if (!window.confirm('Eseguire la deduplicazione del registro? Le entry duplicate verranno rimosse mantenendo la più recente.')) return;
            try {
              const res = await fetch(`${API}/api/booking-register/dedup?dry_run=false`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
              });
              const data = await res.json();
              if (data.entries_removed > 0) {
                toast.success(`Rimossi ${data.entries_removed} duplicati da ${data.duplicate_groups} gruppi (su ${data.total_scanned} entries)`);
                if (viewMode === 'day') fetchDayEntries();
                else fetchMonthEntries();
              } else {
                const msg = `Nessun duplicato trovato (scansionati: ${data.total_scanned} entries, gruppi: ${data.duplicate_groups})`;
                toast.info(msg);
                alert(`DETTAGLIO DEDUP:\n\nEntries scansionate: ${data.total_scanned}\nGruppi duplicati: ${data.duplicate_groups}\nDa rimuovere: ${data.entries_to_remove}\nRimossi: ${data.entries_removed}\n\n${JSON.stringify(data.details?.slice(0,5), null, 2)}`);
              }
            } catch (e) { toast.error('Errore dedup: ' + e.message); }
          }} data-testid="dedup-register-btn">
            Rimuovi Duplicati
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-xs text-slate-500">Prenotazioni</p>
                  <p className="text-xl font-bold">{entries.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-xs text-slate-500">Totale Prezzi</p>
                  <p className="text-xl font-bold">{formatCurrency(totals.total_price)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-500" />
                <div>
                  <p className="text-xs text-slate-500">Incassato</p>
                  <p className="text-xl font-bold">{formatCurrency(totals.collected)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-xs text-slate-500">Da Incassare</p>
                  <p className="text-xl font-bold">{formatCurrency(totals.to_collect)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-xs text-slate-500">Comm. Piattaforma</p>
                  <p className="text-xl font-bold">{formatCurrency(totals.platform_fee)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={totals.hw_margin < 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                {totals.hw_margin >= 0 ? (
                  <TrendingUp className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
                <div>
                  <p className="text-xs text-slate-500">Margine HW (DIFF)</p>
                  <p className={`text-xl font-bold ${totals.hw_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(totals.hw_margin)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Match Results Panel */}
      {showMatchPanel && matchResults && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5" /> Risultati Incrocio HW
              </span>
              <Button variant="ghost" size="sm" onClick={() => setShowMatchPanel(false)}>
                Chiudi
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">{matchResults.matched}</p>
                <p className="text-sm text-green-600">Corrispondenze</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-700">{matchResults.discrepancies}</p>
                <p className="text-sm text-yellow-600">Discrepanze</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-700">{matchResults.not_found}</p>
                <p className="text-sm text-red-600">Non trovati in HW</p>
              </div>
            </div>

            {matchResults.results?.filter(r => r.match_status !== 'matched').length > 0 && (
              <div className="space-y-2">
                <p className="font-medium">Dettagli discrepanze:</p>
                {matchResults.results.filter(r => r.match_status !== 'matched').slice(0, 10).map((r, i) => (
                  <div key={i} className={`p-3 rounded-lg ${r.match_status === 'discrepancy' ? 'bg-yellow-50' : 'bg-red-50'}`}>
                    <p className="font-medium">{r.register_entry?.booking_code}</p>
                    {r.discrepancies?.map((d, j) => (
                      <p key={j} className="text-sm text-slate-600">• {d}</p>
                    ))}
                    {r.match_status === 'not_found' && (
                      <p className="text-sm text-red-600">Non trovato nel sistema HW</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Summary by Day */}
      {viewMode === 'month' && monthSummary && (
        <Card>
          <CardHeader>
            <CardTitle>Riepilogo Giornaliero - {getMonthName(selectedMonth)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Giorno</th>
                    <th className="px-3 py-2 text-center">N° Pren.</th>
                    <th className="px-3 py-2 text-right">Totale</th>
                    <th className="px-3 py-2 text-right">Incassato</th>
                    <th className="px-3 py-2 text-right">Da Incassare</th>
                    <th className="px-3 py-2 text-right">Margine HW</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(monthSummary.by_day || {}).map(([day, data]) => (
                    <tr key={day} className="hover:bg-slate-50">
                      <td className="px-3 py-2">{formatDate(day)}</td>
                      <td className="px-3 py-2 text-center">{data.count}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(data.total_price)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(data.collected)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(data.to_collect)}</td>
                      <td className={`px-3 py-2 text-right font-medium ${data.hw_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(data.hw_margin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 font-medium">
                  <tr>
                    <td className="px-3 py-2">TOTALE</td>
                    <td className="px-3 py-2 text-center">{monthSummary.totals?.count}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(monthSummary.totals?.total_price)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(monthSummary.totals?.collected)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(monthSummary.totals?.to_collect)}</td>
                    <td className={`px-3 py-2 text-right ${monthSummary.totals?.hw_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(monthSummary.totals?.hw_margin)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {viewMode === 'day' 
              ? `Prenotazioni del ${new Date(selectedDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}`
              : `Tutte le prenotazioni di ${getMonthName(selectedMonth)}`
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p className="text-lg">Nessuna prenotazione</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {viewMode === 'month' && <th className="px-2 py-2 text-left font-medium">Data</th>}
                    <th className="px-2 py-2 text-left font-medium">Immobile</th>
                    <th className="px-2 py-2 text-left font-medium">N° Pren.</th>
                    <th className="px-2 py-2 text-center font-medium">N/R</th>
                    <th className="px-2 py-2 text-center font-medium">PERS.</th>
                    <th className="px-2 py-2 text-left font-medium">Piattaf.</th>
                    <th className="px-2 py-2 text-center font-medium">Dal</th>
                    <th className="px-2 py-2 text-center font-medium">Al</th>
                    <th className="px-2 py-2 text-right font-medium">Prezzo</th>
                    <th className="px-2 py-2 text-right font-medium">Presi</th>
                    <th className="px-2 py-2 text-right font-medium">Da Pren.</th>
                    <th className="px-2 py-2 text-right font-medium">PROP</th>
                    <th className="px-2 py-2 text-right font-medium">PUL</th>
                    <th className="px-2 py-2 text-right font-medium text-amber-700">% PREN</th>
                    <th className="px-2 py-2 text-right font-medium">Tassa</th>
                    <th className="px-2 py-2 text-right font-medium">Carta</th>
                    <th className="px-2 py-2 text-right font-medium">SITO</th>
                    <th className="px-2 py-2 text-right font-medium">PART</th>
                    <th className="px-2 py-2 text-right font-medium">DIFF</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {entries.map(entry => {
                    const submultipleDisplay = entry.submultiple ? `/S${entry.submultiple}` : (entry.max_guests ? `${entry.max_guests}` : '-');
                    return (
                    <tr key={entry.id} className={`hover:bg-slate-50 ${entry.hw_margin < 0 ? 'bg-red-50' : ''}`}>
                      {viewMode === 'month' && <td className="px-2 py-2">{formatDate(entry.entry_date)}</td>}
                      <td className="px-2 py-2 font-medium max-w-32 truncate" title={entry.property_name}>{entry.property_name}</td>
                      <td className="px-2 py-2 font-mono text-xs">{entry.booking_code}</td>
                      <td className="px-2 py-2 text-center">
                        <Badge variant="outline" className={entry.tariff_type === 'N' ? 'bg-orange-100' : 'bg-green-100'}>
                          {entry.tariff_type}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-center text-xs">{submultipleDisplay}</td>
                      <td className="px-2 py-2 text-xs capitalize">{entry.platform?.replace('_', ' ')}</td>
                      <td className="px-2 py-2 text-center text-xs">{formatDate(entry.check_in)}</td>
                      <td className="px-2 py-2 text-center text-xs">{formatDate(entry.check_out)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(entry.total_price)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(entry.collected)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(entry.to_collect)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(entry.owner_share)}</td>
                      <td className="px-2 py-2 text-right">
                        {entry.cleaning_cost > 0 ? (
                          <>
                            {formatCurrency(entry.cleaning_cost)}
                            {entry.cleaning_where === 'on_site' && <span className="text-orange-500">*</span>}
                          </>
                        ) : (
                          <button
                            onClick={() => handleCleaningAlert(entry)}
                            className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-800 cursor-pointer"
                            title="Costo pulizia mancante - clicca per gestire"
                            data-testid={`cleaning-alert-${entry.id}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span className="text-xs">€0,00</span>
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-amber-700">
                        {entry.booking_cleaning_surcharge > 0 ? formatCurrency(entry.booking_cleaning_surcharge) : '-'}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(entry.tourist_tax)}
                        {entry.tourist_tax_where === 'on_site' && <span className="text-orange-500">*</span>}
                      </td>
                      <td className="px-2 py-2 text-right text-slate-500">{formatCurrency(entry.card_fee)}</td>
                      <td className="px-2 py-2 text-right text-blue-600">{formatCurrency(entry.platform_fee)}</td>
                      <td className="px-2 py-2 text-right text-purple-600">{formatCurrency(entry.partner_fee)}</td>
                      <td className={`px-2 py-2 text-right font-bold ${entry.hw_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(entry.hw_margin)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                {/* Totals Row */}
                {totals && (
                  <tfoot className="bg-slate-100 font-medium">
                    <tr>
                      {viewMode === 'month' && <td className="px-2 py-2"></td>}
                      <td className="px-2 py-2" colSpan={7}>TOTALI</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.total_price)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.collected)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.to_collect)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.owner_share)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.cleaning_cost)}</td>
                      <td className="px-2 py-2 text-right text-amber-700">{formatCurrency(totals.booking_cleaning_surcharge)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.tourist_tax)}</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totals.card_fee)}</td>
                      <td className="px-2 py-2 text-right text-blue-600">{formatCurrency(totals.platform_fee)}</td>
                      <td className="px-2 py-2 text-right text-purple-600">{formatCurrency(totals.partner_fee)}</td>
                      <td className={`px-2 py-2 text-right font-bold ${totals.hw_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(totals.hw_margin)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
              
              <div className="mt-4 text-xs text-slate-500">
                <span className="text-orange-500">*</span> = Da incassare sul posto
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modale Alert Pulizia Mancante */}
      {cleaningAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="cleaning-alert-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Costo pulizia mancante</h3>
                <p className="text-sm text-slate-500">{cleaningAlert.property_name}</p>
              </div>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              L'immobile <strong>{cleaningAlert.property_name}</strong> non ha un costo pulizia registrato. 
              Inserisci l'importo o conferma che non ci sono costi.
            </p>
            
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-slate-600">€</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={cleaningInput}
                onChange={e => setCleaningInput(e.target.value)}
                placeholder="Inserisci costo pulizia..."
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
                data-testid="cleaning-cost-input"
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={submitCleaningCost}
                disabled={!cleaningInput || parseFloat(cleaningInput) <= 0}
                className="flex-1 bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="submit-cleaning-btn"
              >
                Salva e aggiorna
              </button>
              <button
                onClick={confirmNoCleaning}
                className="flex-1 border border-slate-300 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                data-testid="confirm-no-cleaning-btn"
              >
                Confermo: nessun costo
              </button>
            </div>
            
            <button
              onClick={() => setCleaningAlert(null)}
              className="mt-3 w-full text-center text-xs text-slate-400 hover:text-slate-600"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegistroPrenotazioni;
